import { groupBy, isUndefined, keys, round } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import {
    nbPerPageLabelShorty,
} from '../../../referencial/constants/ReferencialConstants'
import { exportFile, formatData } from '../../../utils/ExportDataUtil'
import useListIndexed from 'utils/customHook/useListIndexed'
import DtoSearchTaxon from 'quality/dto/taxons/DtoSearchHydrobio'
import DtoOperation from 'quality/dto/operation/DtoOperation'
import ProgressCard from 'components/card/ProgressCard'
import OperationAction from '../operation/actions/OperationAction'
import { CardTable } from '../../../components/datatable/NewTable'
import useBoolean from 'utils/customHook/useBoolean'
import useActions from 'utils/customHook/useActions'
import ExportFileModal from 'components/modal/ExportFileModal'
import ExportAction from 'export/actions/ExportAction'
import { calculateTaxonThresholdsResult } from 'utils/QualityThresholdUtils'
import { filterThresholdLevel } from 'utils/AnalyseUtils'
import DtoQualityThreshold from 'quality/dto/QualityThreshold/DtoQualityThreshold'
import { getFullDate } from 'utils/DateUtil'
import { hasValue } from 'utils/NumberUtil'
import { sumBy, keyBy } from 'lodash'

const stationTableHeaders = ['stationCode', 'cityCode', 'cityLabel', 'stationName', 'identifiedTaxons', 'nbSample', 'withEnumerations', 'occurrenceFrequency']
const HYDROBIO_HEADER = ['stationCode', 'stationName', 'sampleDate', 'taxon', 'phaseA', 'phaseB', 'phaseC', 'phaseCbis', 'enumeration', 'abundance']
// const maxNbElements = 25000

// use displayAll if needed
const SampleHydrobioPanel = ({
    hydrobioList = [],
    filter = {},
    researchCriterias = {},
    thresholds = [],
    thresholdLevel,
}) => {
    const [operations, setOperations] = useState([])
    const [progress, setProgress] = useState(0)
    const [dataLoaded, setDataLoaded] = useState(false)

    const {
        qualitometers,
        citiesIndex,
        typeEnvironmentModels,
        taxonsList,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        citiesIndex: store.CityReducer.citiesIndex,
        typeEnvironmentModels: store.ExportReducer.typeEnvironmentModels,
        taxonsList: store.TaxonReducer.taxons,
    }), shallowEqual)

    const dispatch = useDispatch()

    const {
        value: isExportModalOpen,
        setTrue: openExportModal,
        setFalse: closeExportModal,
    } = useBoolean(false)

    const qualitometersIndex = useMemo(() => keyBy(qualitometers, 'id'), [qualitometers])
    const taxonsListIndex = useMemo(() => keyBy(taxonsList, 'id'), [taxonsList])

    useActions(() => {
        return {
            exportList: [{
                onClick: openExportModal,
                label: i18n.excel,
            }],
        }
    }, [])

    const groupedByQualito = useMemo(() => groupBy(hydrobioList, 'qualitometer'), [hydrobioList])

    useEffect(() => {
        const stationIds = keys(groupedByQualito)
        if (stationIds.length) {
            dispatch(OperationAction.getAllHydrobioOperation(stationIds, setProgress)).then(op => {
                setOperations(op.map(o => new DtoOperation(o)))
                setDataLoaded(true)
            })
        } else {
            setDataLoaded(true)
        }
        return () => {
            setOperations([])
            setDataLoaded(false)
            setProgress(0)
        }
    }, [groupedByQualito])


    const formattedHydrobios = useMemo(() => {
        const formatted = hydrobioList.map(t => ({
            ...t,
            ...calculateTaxonThresholdsResult(t, thresholds),
        }))
        return hasValue(thresholdLevel) ? formatted.filter(a => filterThresholdLevel(a, thresholdLevel)) : formatted
    }, [thresholdLevel, thresholds])

    const formattedData = useMemo(() => {
        const groupedByOperation = groupBy(formattedHydrobios, l => `${l.qualitometer}#${l.operation}`)
        return formattedHydrobios.map(a => {
            const station = qualitometersIndex[a.qualitometer]
            const taxon = taxonsListIndex[a.taxon]

            // const operationHydrobios = uniqBy(groupedByOperation[a.operation], 'taxon')
            const operationsPhases = sumBy(groupedByOperation[`${a.qualitometer}#${a.operation}`], 'result')

            const abundanceCalculated = operationsPhases && (a.result * 100) / operationsPhases
            const abundance = abundanceCalculated ? `${round(abundanceCalculated, 2)}%` : i18n.incalculable
            return {
                info: {
                    qualitometerId: a.qualitometer,
                    operationId: a.operation,
                    taxonId: a.taxon,
                },
                stationCode: { value: station?.code },
                stationName: { value: station?.name },
                sampleDate: { value: getFullDate(a.operationDate) },
                taxon: { value: taxon?.labelWithCode },
                phaseA: { value: a.enumerationA || 0 },
                phaseB: { value: a.enumerationB || 0 },
                phaseC: { value: a.enumerationC || 0 },
                phaseCbis: { value: a.enumerationC2 || 0 },
                enumeration: { value: a.result, color: a.color },
                abundance: { value: abundance },
            }
        })
    }, [qualitometersIndex, formattedHydrobios, taxonsListIndex])

    const exportStates = extension => {
        const dataWithHeaders = formattedData.length ? [{ ...formattedData[0], headers: HYDROBIO_HEADER }, ...formattedData.slice(1)] : []
        dispatch(ExportAction.export(formatData(dataWithHeaders), extension, i18n.taxonsDetail))
    }

    const exportTypes = useMemo(() => {
        const models = typeEnvironmentModels.map(model => ({
            name: model,
            formats: [{
                type: i18n.excelFile,
                callback: () => dispatch(ExportAction.exportResearchModel(researchCriterias, model)),
            }],
        }))
        return [
            {
                name: i18n.resultsTable,
                formats: [{
                    type: i18n.excelFile,
                    callback: () => exportStates('xlsx'),
                }, {
                    type: i18n.csvFile,
                    callback: () => exportStates('csv'),
                }],
            },
            {
                name: `${i18n.exportFull} CSV`,
                formats: [{
                    type: i18n.csvFile,
                    callback: () => {
                        dispatch(ExportAction.exportResearchFullCSV(researchCriterias))
                        closeExportModal()
                    },
                }],
            },
            ...models,
        ]
    }, [researchCriterias, typeEnvironmentModels, formattedData])

    const filteredOperations = useMemo(() => {
        return !isUndefined(filter.support) ? operations.filter(o => `${o.support}` === filter.support) : operations
    }, [filter, operations])

    const groupQualitometers = useListIndexed(qualitometers, 'id')

    const stationsData = useMemo(() => {
        const groupedByStations = groupBy(hydrobioList, 'qualitometer')
        return keys(groupedByStations).map(key => {
            const station = groupQualitometers[key]
            const city = station?.townCode ? citiesIndex[station.townCode] : null

            const listHydrobios = groupedByStations[key]
            const identifiedTaxons = keys(groupBy(listHydrobios, 'taxon'))

            const groupedHydrobios = groupBy(listHydrobios, 'operation')
            const samples = filteredOperations.filter(o => o.qualitometer === parseInt(key))

            const operationsWithEnumerations = keys(groupedHydrobios).filter(gtKey => groupedHydrobios[gtKey].filter(t =>
                t.enumerationA ||
                t.enumerationB ||
                t.enumerationC ||
                t.enumerationC2 ||
                t.enumerationP1 ||
                t.enumerationP2 ||
                t.enumerationP3 ||
                t.enumerationP4 ||
                t.enumerationP5 ||
                t.enumerationP6 ||
                t.enumerationP7 ||
                t.enumerationP8 ||
                t.enumerationP9 ||
                t.enumerationP10 ||
                t.enumerationP11 ||
                t.enumerationP12,
            ).length)

            const occurrenceFrequency = samples.length && (operationsWithEnumerations.length / samples.length) * 100
            const roundedOccurrence = occurrenceFrequency ? `${round(occurrenceFrequency, 2)}%` : i18n.incalculable

            return {
                stationCode: { value: station?.code },
                cityCode: { value: station?.townCode },
                cityLabel: { value: city?.name },
                stationName: { value: station?.name },
                identifiedTaxons: { value: identifiedTaxons.length, format: '0', cellType: 'number' },
                nbSample: { value: samples.length, format: '0', cellType: 'number' },
                withEnumerations: { value: operationsWithEnumerations.length, format: '0', cellType: 'number' },
                occurrenceFrequency: { value: roundedOccurrence },
            }
        })
    }, [citiesIndex, groupQualitometers, filteredOperations, hydrobioList])

    return dataLoaded ? (
        <>
            <CardTable
                title={i18n.stations}
                rows={stationsData}
                headers={stationTableHeaders}
                actions={[{
                    icon: 'download',
                    tooltip: i18n.export,
                    onClick: () => {
                        exportFile({
                            data: stationsData.length ? [{ ...stationsData[0], headers: stationTableHeaders }, ...stationsData.slice(1)] : [],
                            exportType: 'xlsx',
                            titleFile: `${i18n.research}-${i18n.quality}`,
                        })
                    },
                }]}
                rowsPerPageOptions={nbPerPageLabelShorty}
                data-cy={'station_table'}
            />
            <ExportFileModal
                open={isExportModalOpen}
                onClose={closeExportModal}
                data={exportTypes}
            />
        </>
    ) : <ProgressCard progress={progress} />
}

SampleHydrobioPanel.propTypes = {
    hydrobioList: PropTypes.arrayOf(PropTypes.instanceOf(DtoSearchTaxon)),
    filter: PropTypes.shape({}),
    researchCriterias: PropTypes.shape({}),
    thresholds: PropTypes.arrayOf(PropTypes.instanceOf(DtoQualityThreshold)),
    thresholdLevel: PropTypes.string,
}

export default SampleHydrobioPanel